/**
 * Session-expiry helpers for the login token (`compass_token`).
 *
 * The app uses a 24h JWT stored in localStorage and attached per-page as an
 * `Authorization: Bearer` header. When it expires the token stays in storage,
 * so `ProtectedRoute` (which only checks token PRESENCE) never redirects and
 * every `/api` call silently 401s — the user's "changes stop saving / new
 * data won't load until I log out and back in" bug.
 *
 * This module lets the SessionExpiryManager watch the token's built-in `exp`
 * and drive a friendly "session expiring / expired" prompt, plus renew a
 * still-valid session in place (no re-login) via `POST /api/auth/renew`.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "compass_token";

// Prompt the user to renew this long BEFORE the token's exp.
export const WARN_BEFORE_MS = 5 * 60 * 1000; // 5 minutes

// window CustomEvent names the manager listens for.
export const SESSION_LOST_EVENT = "compass:session-lost";

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Decode a JWT payload WITHOUT verifying the signature — purely to read the
 * `exp` claim for a UX timer. NEVER trust this for authorization. Returns
 * null for anything that isn't a well-formed JWT.
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/** Absolute expiry (ms epoch) of the current token, or null if unknown. */
export function getTokenExpiryMs(token = getToken()) {
  const p = decodeJwtPayload(token);
  if (!p || typeof p.exp !== "number") return null;
  return p.exp * 1000;
}

/** Milliseconds until the current token expires, or null if unknown. */
export function msUntilExpiry(token = getToken()) {
  const exp = getTokenExpiryMs(token);
  if (exp == null) return null;
  return exp - Date.now();
}

/**
 * True only when the token's `exp` is READABLE and already at/after now.
 * An opaque token (no readable `exp`) returns false — we can't decide, so we
 * let the request layer / server 401 handle it rather than treating it as
 * logged out. UX/routing hint only; never trusted for authorization.
 */
export function isTokenExpired(token = getToken()) {
  const ms = msUntilExpiry(token);
  return ms != null && ms <= 0;
}

/**
 * Exchange the CURRENT (still-valid) token for a fresh one and persist it.
 * Returns the new token on success; throws if the backend refuses (e.g. the
 * token already expired — a fresh token can't be minted from an expired one,
 * so the caller falls back to the re-login prompt).
 *
 * `__noSessionPrompt` tells the axios interceptor NOT to fire the global
 * session-lost event for this request — the caller handles its own 401.
 */
export async function renewSession() {
  const token = getToken();
  if (!token) throw new Error("no token to renew");
  const res = await axios.post(
    `${API}/api/auth/renew`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
      __noSessionPrompt: true,
    },
  );
  const next = res.data && res.data.access_token;
  if (!next) throw new Error("renew response missing access_token");
  localStorage.setItem(TOKEN_KEY, next);
  return next;
}
